import React, { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTour } from '../store/tour';

type Rect = { top: number; left: number; width: number; height: number };

function useTargetRect(selector: string) {
  const [rect, setRect] = useState<Rect | null>(null);
  useEffect(() => {
    if (!selector) {
      setRect(null);
      return;
    }
    const update = () => {
      const el = document.querySelector(selector) as HTMLElement | null;
      if (!el) {
        setRect(null);
        return;
      }
      const box = el.getBoundingClientRect();
      setRect({ top: box.top, left: box.left, width: box.width, height: box.height });
    };
    update();
    const id = window.setInterval(update, 400);
    window.addEventListener('resize', update);
    window.addEventListener('scroll', update, true);
    return () => {
      clearInterval(id);
      window.removeEventListener('resize', update);
      window.removeEventListener('scroll', update, true);
    };
  }, [selector]);
  return rect;
}

export default function TourOverlay() {
  const { steps, index, active, next, back, stop, setSteps } = useTour();
  const step = useMemo(() => steps[index], [steps, index]);
  const selector = step?.selector || '';
  const rect = useTargetRect(selector);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    if (!active) {
      setReady(false);
      return;
    }
    const id = window.setTimeout(() => setReady(true), 80);
    return () => window.clearTimeout(id);
  }, [active, index]);

  useEffect(() => {
    if (!active || !step) return;
    if (rect) return;
    const filtered = steps.filter((s) => s.id !== step.id);
    if (!filtered.length) {
      stop();
      return;
    }
    setSteps(filtered);
  }, [active, rect, setSteps, step, steps, stop]);

  if (!active || !step || !selector || !rect || !ready) return null;

  const viewportWidth = window.innerWidth;
  const viewportHeight = window.innerHeight;
  const panelWidth = Math.min(340, viewportWidth - 24);
  const gap = 16;
  const cardHeightEstimate = 188;
  const preferTop = step.position === 'top';
  const roomAbove = rect.top;
  const roomBelow = viewportHeight - (rect.top + rect.height);
  const placeAbove = preferTop ? roomAbove > 120 : roomAbove > roomBelow && roomAbove > 140;
  const desiredTop = placeAbove
    ? rect.top - cardHeightEstimate - gap
    : rect.top + rect.height + gap;
  const top = Math.max(12, Math.min(desiredTop, viewportHeight - cardHeightEstimate - 12));
  const desiredLeft = rect.left + rect.width / 2 - panelWidth / 2;
  const left = Math.max(12, Math.min(desiredLeft, viewportWidth - panelWidth - 12));

  return createPortal(
    <div className="fixed inset-0 z-[2000]">
      <div className="absolute inset-0 bg-black/55" onClick={stop} />
      <div
        className="absolute rounded-xl border-2 border-white/80 pointer-events-none"
        style={{
          top: Math.max(8, rect.top - 8),
          left: Math.max(8, rect.left - 8),
          width: rect.width + 16,
          height: rect.height + 16,
          boxShadow: '0 0 0 9999px rgba(0,0,0,0.28)',
        }}
      />
      <div
        className="absolute bg-white text-black rounded-2xl shadow-2xl p-4 space-y-2 pointer-events-auto"
        style={{
          top,
          left,
          width: panelWidth,
        }}
      >
        <p className="text-xs uppercase tracking-[0.3em] text-gray-500">
          Step {index + 1} of {steps.length}
        </p>
        <h3 className="text-lg font-semibold">{step.title}</h3>
        <p className="text-sm text-gray-700">{step.body}</p>
        <div className="flex items-center justify-between pt-2">
          <button
            className="px-3 py-1.5 rounded-full border border-gray-300 text-sm"
            onClick={index === 0 ? stop : back}
          >
            {index === 0 ? 'Skip tour' : 'Back'}
          </button>
          <div className="flex items-center gap-2">
            <button
              className="px-3 py-1.5 rounded-full border border-gray-300 text-xs md:text-sm"
              onClick={stop}
            >
              Don&apos;t show again
            </button>
            <button className="px-3 py-1.5 rounded-full bg-black text-white text-sm" onClick={next}>
              {index === steps.length - 1 ? 'Finish' : 'Next'}
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}
